import { Injectable } from '@angular/core';
import cloneDeep from 'lodash/cloneDeep';
import kebabCase from 'lodash/kebabCase';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { IdentifierKey } from '@models';
import {
  AssetClass,
  ChartCalculationType,
  CommonConfig,
  CurrencyCode,
  FundId,
  FundIdentifier,
  FundSectionLink,
  FundShareClassId,
  GlobalConfig,
  LiteratureConfig,
  LiteraturePageLayoutConfig,
  MifidAge,
  PerformanceType,
  PortfolioComponentId,
  ProductConfig,
  ProductTabSet,
  ProductType,
  SegmentId,
  ShareClassCode,
  StatisticId,
  StatisticSuppressionSet,
  SuppressionSet,
  TabSet,
  WebProductTaxonomy,
} from '@types';
import { ApiUrls, AppStateService } from './app-state.service';
import { Logger } from '@utils/logger';
import { SegmentService } from './segment.service';
import { GlobalConfigService } from './global-config-service';
import {
  convertCsvFields,
  mergeConfig,
  splitField,
} from '@utils/data/object-utils';
import {
  getCached1YearAgoStd,
  getCached5YearAgoStd,
  getCached6MonthsAgoStd,
} from '@utils/il8n/moment-il8n';
const logger = Logger.getLogger('SiteConfigService');

export enum BenchmarksToShow {
  ZERO = 0,
  ONE = 1,
  TWO = 2,
  ALL = Number.MAX_VALUE,
}

@Injectable({
  providedIn: 'root',
})
export class SiteConfigService {
  private isPopulated$ = new BehaviorSubject<boolean>(false);
  /**
   * For now, we're just exposing config as public properties
   * TODO: replace these with methods for specific bits of state
   */
  public apiUrls?: ApiUrls;
  public segmentId: SegmentId;
  public common: CommonConfig;
  public product: ProductConfig = {};
  public literature: LiteratureConfig = {};
  public literaturePageLayoutConfig: LiteraturePageLayoutConfig;
  private currencyListByPerformance: string[] = [];
  public currencyListByPerformance$: BehaviorSubject<string[]>;

  private tabsetCache: TabSet[] = [];

  constructor(
    private segmentService: SegmentService,
    private globalConfigService: GlobalConfigService,
    protected appStateService: AppStateService
  ) {
    this.currencyListByPerformance$ = new BehaviorSubject(
      this.currencyListByPerformance
    );
    combineLatest([
      this.globalConfigService.getGlobalConfigSubject$(),
      this.segmentService.getCurrentSegmentId$(),
    ]).subscribe(this.mapConfigData);
  }

  private mapConfigData = ([globalConfig, segmentId]: [
    GlobalConfig,
    SegmentId
  ]): void => {
    logger.debug('args', globalConfig, segmentId);

    if (!segmentId) {
      segmentId = this.segmentService.getDefaultSegmentId();
    }

    const siteConfig = globalConfig.siteConfiguration;

    if (!siteConfig) {
      return; // isPopulated will remain false unless previously populated successfully
    }

    this.segmentId = segmentId;
    this.apiUrls = this.appStateService.getApiUrls();
    this.common = siteConfig.site;

    if (siteConfig?.funds) {
      const genericProductConfig = cloneDeep(siteConfig.funds.all) || {};
      const segmentProductConfig = cloneDeep(siteConfig.funds[segmentId]) || {};
      this.product = convertCsvFields(
        mergeConfig(genericProductConfig, segmentProductConfig)
      );
      // pull in funds in common lists NGC-
      if (this.product.general?.activeListCommon?.length) {
        const commonFunds: FundShareClassId[][] = this.product.general.activeListCommon.map(
          (index) => splitField(siteConfig.funds.commonActiveLists[index])
        ) as FundShareClassId[][];
        this.product.general.activeList = this.product.general.activeList.concat(
          ...commonFunds
        );
      }
    }

    if (siteConfig?.literature) {
      const genericLiteratureConfig =
        cloneDeep(siteConfig.literature?.all) || {};
      const segmentLiteratureConfig =
        cloneDeep(siteConfig.literature[segmentId]) || {};
      const literatureUrls = {
        litDetailUrl: siteConfig.literature.litdetail || '',
        litOrderCheckoutUrl: siteConfig.literature.litordercheckout || '',
        litOrderHistoryDetailUrl:
          siteConfig.literature.litorderhistorydetail || '',
        orderConfirm: siteConfig.literature.orderconfirm || '',
        orderHistoryList: siteConfig.literature.orderhistorylist || '',
        orderSuccess: siteConfig.literature.ordersuccess || '',
        orderCheckoutAddress: siteConfig.literature.ordercheckoutaddress || '',
        litListing: siteConfig.literature.litlisting || '',
        myLiteratureLandingUrl: siteConfig.literature.myliteraturelanding || '',
        downloadExcelIdentifiers_csv:
          siteConfig.literature.downloadExcelIdentifiers_csv || '',
      };
      this.literature = convertCsvFields(
        mergeConfig(genericLiteratureConfig, {
          ...segmentLiteratureConfig,
          ...literatureUrls,
        })
      );
    }
    logger.debug(
      'mapConfigData',
      this.segmentId,
      this.apiUrls,
      this.product,
      this.literature
    );
    this.isPopulated$.next(true);
  };

  /*******************************************************************
   * helper methods
   */

  public getDefaultIdentifierKey(
    webProductTaxonomy: WebProductTaxonomy
  ): IdentifierKey {
    if (!this.product.general.keyIdentifier) {
      return null;
    }
    return this.product.general.keyIdentifier[webProductTaxonomy] || null;
  }

  public getFundTabSet(fundId: FundId, isLitPreview = false): TabSet {
    // 2. Get tab config for ProductType
    const productSet: ProductTabSet = this.product.tabs;
    if (!productSet) {
      logger.info('No tabsets configured');
      return null;
    }

    // 3. Calculate set name, by looping assignments
    let setName = 'default';
    if (productSet.assignments) {
      setName = Object.entries(productSet.assignments) // turn assignments object into an array
        .reduce((name, [key, funds]) => {
          return funds.includes(fundId) ? key : name;
        }, setName);
    }

    // 4. for lit-preview page we use predefined set with documents only for all funds
    if (isLitPreview) {
      setName = 'lit-preview';
    }

    // 5. Get correct TabSet
    const tabSet: TabSet = productSet.sets.find(
      (set: TabSet): boolean => setName === set.name
    );
    if (!tabSet) {
      logger.info(`No tabset configured for ${this.segmentId} ${setName}`);
      return null;
    }

    return tabSet;
  }

  /**
   * returns an absolute link for a fund's share class.
   * The FundId is required!
   * Other params will be added to the url if they are passed
   */
  public getFundLink(
    fundId: FundId,
    shareClassCode?: ShareClassCode,
    fundName?: string,
    identifier?: FundIdentifier,
    isLitPreview = false
  ): string {
    // 1. Get TabSet
    // 1a. cache added, so when we read 1500 times FundLink on PPSS page we don't have to process/find tabset 1500 times
    const tabSetCacheKey = this.getTabSetCacheKey(fundId, isLitPreview);

    let tabSet: TabSet;
    if (tabSetCacheKey in this.tabsetCache) {
      tabSet = this.tabsetCache[tabSetCacheKey];
    } else {
      tabSet = this.getFundTabSet(fundId, isLitPreview);
      this.tabsetCache[tabSetCacheKey] = tabSet;
    }

    if (!tabSet) {
      return null;
    }

    // 2. Get base link from TabSet
    let link: string = tabSet.page;
    if (!link) {
      return null;
    }

    // 3. add spaBaseUrl prefix
    if (this.apiUrls && this.apiUrls.spaBaseUrl) {
      link = this.apiUrls.spaBaseUrl + link;
    }

    // 4. Add FundId
    link = `${link}/${fundId}`;

    // 5. Optionally add ShareClassCode
    if (shareClassCode) {
      link = `${link}/${shareClassCode}`;
    }

    // 6. Add fundName in kebab case for SEO
    if (fundName) {
      link = `${link}/${kebabCase(fundName)}`.replace(
        'clear-bridge',
        'clearbridge'
      );
    }

    // 7. Optionally add default Identifier for SEO
    // TODO: should this be kebabcase too?
    if (identifier) {
      link = `${link}/${identifier}`;
    }
    return link;
  }

  // returns cache key for tabset
  private getTabSetCacheKey(fundId: FundId, isLitPreview = false) {
    return fundId + (isLitPreview ? '_1_' : '_0_') + this.segmentId;
  }

  public getFundSectionLinks(
    fundId: FundId,
    shareClassCode?: ShareClassCode,
    fundName?: string,
    identifier?: FundIdentifier
  ): FundSectionLink[] {
    const baseLink: string = this.getFundLink(
      fundId,
      shareClassCode,
      fundName,
      identifier
    );
    if (!baseLink) {
      return null;
    }

    const tabSetCacheKey = this.getTabSetCacheKey(fundId);
    let tabSet: TabSet = this.tabsetCache[tabSetCacheKey];

    if (!tabSet) {
      tabSet = this.getFundTabSet(fundId);
      this.tabsetCache[tabSetCacheKey] = tabSet;
    }

    return tabSet.tabs.map(
      (tab: string): FundSectionLink => ({
        link: `${baseLink}#${tab}`,
        // TODO: if this was a service, could use the translate service
        labelKey: `products.section-tab-${tab}`,
      })
    );
  }

  public isActiveShareClass(shareClassKey: FundShareClassId): boolean {
    return this.product.general.activeList.includes(shareClassKey);
  }

  public isSoftLaunchShareClass(shareClassKey: FundShareClassId): boolean {
    return this.product.general.softLaunchList.includes(shareClassKey);
  }

  public isSoftClosedShareClass(shareClassKey: FundShareClassId): boolean {
    return this.product.general.softClosedList?.includes(shareClassKey);
  }

  /**
   * Indicates if ShareClass should appear in PPSS etc i.e is either Active or SoftLaunch
   */
  public isDisplayedShareClass(
    shareClassKey: FundShareClassId,
    includeSoftClosed = false
  ): boolean {
    return (
      this.isActiveShareClass(shareClassKey) ||
      this.isSoftLaunchShareClass(shareClassKey) ||
      (includeSoftClosed
        ? this.isSoftClosedShareClass(shareClassKey)
        : includeSoftClosed)
    );
  }

  // TODO: define productType type
  public getFundContentCategories(
    productType: string,
    location: string
  ): string[] {
    return this.product.overview[productType]?.fundContent?.find(
      (cats) => cats.location === location
    )?.categories;
  }

  public getRiskFactorContentCategory() {
    return this.product.overview.riskFactorContentCategory;
  }

  public mustHidePerformanceData(
    performanceDateStd: string,
    isLoggedIn: boolean = false
  ): boolean {
    let hidePerformanceData = false;
    const sixMonthsAgoStd = getCached6MonthsAgoStd();
    const oneYearAgoStd = getCached1YearAgoStd();
    if (this.product?.general.showMDashForUnder1Year) {
      hidePerformanceData = this.hidePerformanceDataRule(
        performanceDateStd,
        isLoggedIn,
        oneYearAgoStd
      );
    } else if (this.product?.general.showMDashForUnder6Months) {
      hidePerformanceData = this.hidePerformanceDataRule(
        performanceDateStd,
        isLoggedIn,
        sixMonthsAgoStd
      );
    }
    return hidePerformanceData;
  }

  // to override supressPerformance data for loggedin user on CA
  private hidePerformanceDataRule(
    performanceDateStd: string,
    isLoggedIn: boolean,
    cachedStdDate: string
  ): boolean {
    return this.product.general?.displayPerformanceDataForUnder1Year &&
      isLoggedIn &&
      this.isCanada()
      ? false
      : performanceDateStd > cachedStdDate;
  }

  /**
   * Only applies to Germany+Austria
   * Returns value indicating if fund is less than 1, 1 to 5 or over 5 years old
   * All other sites returns null
   */
  public getMifidAge(inceptionDateStd: string): MifidAge {
    if (this.isSiteDynamicLabels()) {
      if (inceptionDateStd < getCached5YearAgoStd()) {
        return MifidAge.OVER_FIVE;
      } else if (inceptionDateStd > getCached1YearAgoStd()) {
        return MifidAge.UNDER_ONE;
      } else {
        return MifidAge.ONE_TO_FIVE;
      }
    }
    return null;
  }

  public hideRiskStatisticsBenchmark(): boolean {
    return this.product.performance?.hideRiskStatisticsBenchmark
      ? this.product.performance.hideRiskStatisticsBenchmark
      : false;
  }

  public hidePerformanceCurrency(
    webProductTaxonomy: WebProductTaxonomy
  ): boolean {
    return this.product.performance.hideCurrency
      ? this.product.performance.hideCurrency.includes(webProductTaxonomy)
      : false;
  }
  public getBenchmarksToShowOnOverview(fundId: FundId): number {
    if (this.product.overview.showAllAssociatedBenchmarkInfo) {
      return BenchmarksToShow.ALL;
    } else {
      return this.getMaxNumberOfBenchmarksToShow(fundId);
    }
  }

  public getMaxNumberOfBenchmarksToShow(fundId: FundId): number {
    if (this.product.general.noBenchmarkList?.includes(fundId)) {
      return BenchmarksToShow.ZERO;
    }
    if (this.product.general.twoBenchmarkList?.includes(fundId)) {
      return BenchmarksToShow.TWO;
    }
    if (this.product.general.allBenchmarkList?.includes(fundId)) {
      return BenchmarksToShow.ALL;
    }
    return BenchmarksToShow.ONE;
  }

  public getBenchmarkOrder(webProductTaxonomy: WebProductTaxonomy): string[] {
    if (
      this.product.performance.benchmarkOrder &&
      webProductTaxonomy in this.product.performance.benchmarkOrder &&
      this.product.performance.benchmarkOrder[webProductTaxonomy].length
    ) {
      return this.product.performance.benchmarkOrder[webProductTaxonomy];
    }
    return ['INST1', 'INST2', 'INST3', 'INST4', 'INST5'];
  }

  public getSuppressedFundInfoElements(
    webProductTaxonomy: WebProductTaxonomy
  ): string[] {
    if (
      this.product.overview.fundInfoSuppressionList &&
      webProductTaxonomy in this.product.overview.fundInfoSuppressionList
    ) {
      return this.product.overview.fundInfoSuppressionList[webProductTaxonomy];
    }
    return [];
  }

  public getSuppressedBenchmarks(
    webProductTaxonomy: WebProductTaxonomy
  ): string[] {
    if (
      this.product.performance.suppressBenchmarkData &&
      webProductTaxonomy in this.product.performance.suppressBenchmarkData
    ) {
      return this.product.performance.suppressBenchmarkData[webProductTaxonomy];
    }
    return [];
  }

  public hideAdditionalShareClassLinks(
    webProductTaxonomy: WebProductTaxonomy
  ): boolean {
    return this.product.performance.hideAddShLinkList
      ? this.product.performance.hideAddShLinkList.includes(webProductTaxonomy)
      : false;
  }

  public showPeerGroupCategoryAverageAddition(
    webProductTaxonomy: WebProductTaxonomy
  ): boolean {
    return this.product?.performance?.showPeerGroupCategoryAverageAddition?.includes(
      webProductTaxonomy
    );
  }

  public showRatings(productType: ProductType): boolean {
    if (
      this.product.ppss.showRatings &&
      productType in this.product.ppss.showRatings
    ) {
      return this.product.ppss.showRatings[productType];
    }
    return false;
  }

  public showOverviewRatings(
    webProductTaxonomy: WebProductTaxonomy,
    type: string
  ): boolean {
    if (
      this.product.overview.ratings &&
      webProductTaxonomy in this.product.overview.ratings
    ) {
      return this.product.overview.ratings[webProductTaxonomy].includes(type);
    }
    return false;
  }

  public showYields(productType: ProductType): boolean {
    if (
      this.product.ppss.showYields &&
      productType in this.product.ppss.showYields
    ) {
      return this.product.ppss.showYields[productType];
    }
    return false;
  }

  /**
   * It will return the show article 6 status from site config
   */
  public showArticle6(productType: ProductType): boolean {
    if (
      this.product.ppss.showArticle6 &&
      productType in this.product.ppss.showArticle6
    ) {
      return this.product.ppss.showArticle6[productType];
    }
    return false;
  }

  public hideNavChange(productType: ProductType): boolean {
    if (
      this.product.ppss.hideNavChange &&
      productType in this.product.ppss.hideNavChange
    ) {
      return this.product.ppss.hideNavChange[productType];
    }
    return false;
  }

  public hideFactsheet(productType: ProductType): boolean {
    if (
      this.product.ppss.hideFactsheet &&
      productType in this.product.ppss.hideFactsheet
    ) {
      return this.product.ppss.hideFactsheet[productType];
    }
    return false;
  }

  public showIdentifiers(productType: ProductType): boolean {
    if (
      this.product.ppss.showIdentifiers &&
      productType in this.product.ppss.showIdentifiers
    ) {
      return this.product.ppss.showIdentifiers[productType];
    }
    return false;
  }

  public showDownloadExcelButton(productType: ProductType): boolean {
    if (
      this.product.ppss.showExcelDownload &&
      productType in this.product.ppss.showExcelDownload
    ) {
      return this.product.ppss.showExcelDownload[productType];
    }
    return false;
  }

  public getFundIdentifiers(productType: ProductType): string[] {
    if (
      this.product.ppss.fundIdentifiers &&
      productType in this.product.ppss.fundIdentifiers
    ) {
      return this.product.ppss.fundIdentifiers[productType];
    }
    return [];
  }

  /**
   * Fetch the config from site common, if favorite funds is enabled.
   * This config isn't specific to any product type.
   */
  public isFavoriteFundsEnabled(): boolean {
    return this.common.enableFavoriteFunds;
  }

  /**
   * Return boolean flag as per config received from BR against specific product type.
   * Default being false, if config not received too.
   * @param productType product type received from component rendered
   */
  public showFavoritesOnly(productType: ProductType): boolean {
    if (
      this.product.ppss.showFavoriteToggle &&
      productType in this.product.ppss.showFavoriteToggle
    ) {
      return this.product.ppss.showFavoriteToggle[productType];
    }

    return false;
  }

  public getPeriodEnds(
    productType: ProductType,
    performanceType: PerformanceType
  ): string[] {
    if (
      this.product.performance.dataRefreshRate &&
      productType in this.product.performance.dataRefreshRate
    ) {
      const dataRefreshRate: Record<PerformanceType, string> = this.product
        .performance.dataRefreshRate[productType];
      if (performanceType in dataRefreshRate) {
        return this.decodePeriodEnds(dataRefreshRate[performanceType]);
      } else {
        return [];
      }
    }
    return [];
  }

  /**
   * Return the performance period code as their respective name array.
   * @param peCode period code
   */
  public decodePeriodEnds(peCode: string): string[] {
    switch (peCode) {
      case 'MQ':
        return ['MONTHLY', 'QUARTERLY'];
      case 'QM':
        return ['QUARTERLY', 'MONTHLY'];
      case 'Q':
        return ['QUARTERLY'];
      case 'M':
        return ['MONTHLY'];
      default:
        return [];
    }
  }

  public getIsPopulated$(): Observable<boolean> {
    return this.isPopulated$.asObservable();
  }

  public getPdsCountry(): string {
    return this.product.site?.country;
  }

  public getPdsLanguage(): string {
    return this.product.site?.language;
  }

  /*******************************************************************
   * Portfolio Config helpers
   */

  public isPortfolioBenchmarkSuppressed(fundId: FundId): boolean {
    const isSuppressed: boolean = this.product.portfolio?.benchmarkSuppression.includes(
      fundId
    );
    if (isSuppressed) {
      logger.debug(`Benchmark suppressed for fundId '${fundId}'`);
    }
    return isSuppressed;
  }

  /**
   * If the component is suppressed, the apprioriate supression set will be returned
   * if not suppressed, this will return null
   */
  public getPortfolioComponentSuppressionSet(
    componentId: PortfolioComponentId,
    fundId: FundId,
    assetClass: AssetClass
  ): SuppressionSet {
    // return first set that matches componentId AND (fundId OR assetClass)
    const suppressionSet: SuppressionSet = this.product.portfolio?.suppressionSets.find(
      (set: SuppressionSet): boolean =>
        set.portfolioComponents.includes(componentId) &&
        (set.funds.includes(fundId) || set.assetClasses.includes(assetClass))
    );
    if (suppressionSet) {
      const reason = suppressionSet.funds.includes(fundId)
        ? `fundId '${fundId}'`
        : `asset class '${assetClass}'`;
      logger.debug(
        `Portfolio Component '${componentId}' is suppressed by set '${suppressionSet.id}' due to ${reason}`
      );
      return suppressionSet;
    }

    // Component not suppressed, so return null
    return null;
  }

  public isPortfolioStatisticSuppressedBySite(
    statisticId: StatisticId
  ): boolean {
    if (this.product.portfolio?.suppressStatistics?.includes(statisticId)) {
      // check if this statistics is suppressed for whole site
      logger.debug(`Statistic '${statisticId}' suppressed for entire site`);
      return true;
    }
    return false;
  }

  public getPortfolioStatisticSuppressionSet(
    statisticId: StatisticId,
    fundId: FundId
  ): StatisticSuppressionSet {
    // return first set that matches statisticId AND fundId
    const suppressionSet: StatisticSuppressionSet = this.product.portfolio?.statisticSuppressionSets.find(
      (set: StatisticSuppressionSet): boolean =>
        set.statistics.includes(statisticId) && set.funds.includes(fundId)
    );
    if (suppressionSet) {
      logger.debug(
        `Portfolio Statistic '${statisticId}' is suppressed by set '${suppressionSet.id}' due to fundId '${fundId}`
      );
      return suppressionSet;
    }
    return null;
  }

  public getCumulativeChartCalculationType(): ChartCalculationType[] {
    switch (this.product.performance?.cumulativeHypoChart) {
      case 'BOTH':
        return [
          ChartCalculationType.HUNDRED_REBASE_POINTS,
          ChartCalculationType.TENK_GROWTH,
        ];
      case '100 rebase':
        return [ChartCalculationType.HUNDRED_REBASE_POINTS];
      case '10k':
        return [ChartCalculationType.TENK_GROWTH];
      default:
        return [];
    }
  }

  public isSiteInternational(): boolean {
    return this.product?.site?.country !== 'US';
  }

  public isSiteUcits(): boolean {
    return (
      this.product?.site?.country !== 'US' &&
      this.product?.site?.country !== 'CA'
    );
  }

  // returns true if site should have dynamic mifid labels i.e. Austria or Germany
  public isSiteDynamicLabels(): boolean {
    return this.product?.performance?.dynamicLabels;
  }

  // temit comes under channel section instead of geographical location
  public isTemit(): boolean {
    return this.appStateService.getChannel() === 'temit';
  }

  // martin currie gpt comes under channel section instead of geographical location
  public isMartinCurrieGpt(): boolean {
    return this.appStateService.getChannel() === 'martin-currie-gpt';
  }

  public isCanada(): boolean {
    return this.getPdsCountry() === 'CA';
  }

  public isLuxembourg(): boolean {
    return this.getPdsCountry() === 'LU';
  }

  public isNjBest(): boolean {
    return (
      this.getPdsCountry() === 'US' &&
      this.common.taxonomyLanguage === 'english---njbest'
    );
  }

  public isSouthAfrica(): boolean {
    return this.getPdsCountry() === 'ZA';
  }

  public isOffshore(): boolean {
    return this.getPdsCountry() === 'NRC';
  }

  public showPop() {
    return !this.isSiteInternational() || this.getPdsCountry() === 'AU';
  }

  public showApplicationPrice() {
    return this.getPdsCountry() === 'AU';
  }

  public showRedemptionPrice() {
    return this.getPdsCountry() === 'AU';
  }

  public isSgHk() {
    return (
      this.product?.site?.country === 'SG' ||
      this.product?.site?.country === 'HK'
    );
  }

  public isBrazil(): boolean {
    return this.getPdsCountry() === 'BR';
  }

  public getDefaultCalcType(productType: ProductType): string {
    if (
      this.product.general.defaultCalcType &&
      productType in this.product.general.defaultCalcType
    ) {
      const calcType = this.product.general.defaultCalcType[productType].trim();
      return calcType !== '' ? calcType : null;
    }
    return null;
  }

  public getAdditionalCalcType(productType: ProductType): string {
    if (
      this.product.general.additionalCalcType &&
      productType in this.product.general.additionalCalcType
    ) {
      const calcType = this.product.general.additionalCalcType[
        productType
      ].trim();
      return calcType !== '' ? calcType : null;
    }
    return null;
  }

  // WDE-1642 : Australia Campaign Page Callout Tracking Issue
  // we only need the spaBaseUrl for dynamic Card links, this
  // function only provides us with spaBaseUrl
  public getSpaBaseUrl(): string {
    return this.apiUrls?.spaBaseUrl || '';
  }

  /**
   * Returns GlobalConfig Site configuration
   */
  public getGlobalConfig(): GlobalConfig {
    return this.globalConfigService?.config;
  }

  public isTraded(fundId: FundId): boolean {
    return fundId && !this.product.general.nonTradedFunds?.includes(fundId);
  }

  /**
   * NGC-12770: Fund managers bios visibility condition.
   * Return boolean as flag present in 'showLinkToBios' in BR config against given taxonomy.
   * @param webProductTaxonomy valid value from pds feed
   */
  public isShowLinkToBiosStatusValidForFundManagers(
    webProductTaxonomy: WebProductTaxonomy
  ): boolean {
    if (this.product.overview.showLinkToBios?.[webProductTaxonomy]) {
      return true;
    }

    return false;
  }

  public isShowYearsOfExperienceValidForFundManagers(
    webProductTaxonomy: WebProductTaxonomy
  ): boolean {
    if (this.product.overview.showYearsOfExperience?.[webProductTaxonomy]) {
      return true;
    }

    return false;
  }

  /**
   * ********** NGC-13646: SA - performance currency list. **********
   */

  /**
   * Get the currency list created by performance.
   */
  public getCurrencyListByPerformance$(): Observable<string[]> {
    return this.currencyListByPerformance$.asObservable();
  }

  // NGC-14200 : SOUTH AFRICA Products - Money Funds not displayed when selected from Select Product Type drop down
  // we need this functionality as constructor doesn't reinitialize performance currency list when new state is loaded because
  // it is being marked complete(). Hence doing this to avoid the problem and get fresh values of performance currency list
  // each time new state is being added.

  /**
   * reinitialize the performance currency list so that it can be populated.
   */
  public reInitializeCurrencyListByPerformance(): void {
    this.currencyListByPerformance = [];
  }

  /**
   * reinitializing the observable too to invoke it during service call.
   */
  public reInitializeCurrencyListByPerformanceSubject(): void {
    this.currencyListByPerformance$ = new BehaviorSubject(
      this.currencyListByPerformance
    );
  }

  /**
   * Push the unique currency code in the list created.
   * @param currencyCode currency code in performance feed
   */
  public createCurrencyListByPerformance(currencyCode: CurrencyCode): void {
    if (
      currencyCode &&
      !this.currencyListByPerformance.includes(currencyCode)
    ) {
      this.currencyListByPerformance.push(currencyCode);
      // Here, 'reference' is getting updated with fresh values for 'currencyListByPerformance$'.
      // This will indirecly reflect whole list on its subcription in ppss service, without actually updating the state.
      // See above, in 'getCurrencyListByPerformance' and well as this method, no next operator is being used.
    }
  }

  /**
   * Determines if Username field should be hidden on sign-in forms
   */
  public hideSignInUserName(): boolean {
    return this.common.hideLoginForm;
  }

  /**
   * Determines if Password field should be hidden on sign-in forms
   */
  public hideSignInPassword(): boolean {
    return this.common.hideLoginForm;
  }
}
